import { useCallback, useRef, useEffect } from 'react';

import { message as antMessage } from 'antd';
import { useTranslation } from 'react-i18next';

// action 回傳 result 的 shape：result.success / result.data.{ job_id, message }
interface ExportActionResult {
  success?: boolean;
  message?: string;
  data?: {
    job_id?: string;
    message?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

type AddJobFn = (jobId: string, jobName: string) => void;

interface UseExportI18nKeys {
  noSelection?: string;
  success?: string;
  error?: string;
  [key: string]: string | undefined;
}

interface UseExportConfig {
  action: (ids: string[]) => Promise<ExportActionResult>; // New: Service method (ids) => Promise
  addJob?: AddJobFn;
  jobName?: string;
  i18nKeys?: UseExportI18nKeys;
  debounceDelay?: number; // Debounce delay in milliseconds
}

/**
 * 共用的匯出 Hook
 *
 * 功能：
 * - 處理匯出邏輯（呼叫 API、啟動背景任務）
 * - 支援 Admin / Operator 兩種角色
 * - 自動處理錯誤提示
 * - 支援多語系
 *
 * @param {Object} options
 * @param {Function} options.addJob - 背景任務管理器的 addJob 函數 (從 Context 取得)
 * @param {string} [options.jobName] - 背景任務名稱 (預設: 'common.button.export')
 *
 * @returns {Function} handleExport(selectedIds) - 匯出處理函數
*/
export function useExport({
  action, // New: Service method (ids) => Promise
  addJob,
  jobName = 'common.button.export',
  i18nKeys = {},
  debounceDelay = 300 // Debounce delay in milliseconds
}: UseExportConfig): (selectedIds: string[] | Set<string>) => Promise<void> {
  const { t, i18n } = useTranslation();
  const exportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExportingRef = useRef(false);

  // 🔍 Debug
  console.log('[useExport] Initialize:', {
    hasAddJob: !!addJob,
    addJobType: typeof addJob,
    jobName
  });

  // 預設的 i18n keys
  const defaultI18nKeys: Required<Pick<UseExportI18nKeys, 'noSelection' | 'success' | 'error'>> = {
    noSelection: 'shared.export.errors.no_selection',
    success: 'shared.export.errors.success',
    error: 'shared.export.errors.error',
  };

  const keys = { ...defaultI18nKeys, ...i18nKeys };

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (exportTimeoutRef.current) {
        clearTimeout(exportTimeoutRef.current);
      }
    };
  }, []);

  const handleExport = useCallback(async (selectedIds: string[] | Set<string>): Promise<void> => {
    // 如果正在匯出，忽略點擊
    if (isExportingRef.current) {
      console.log('[useExport] Export already in progress, ignoring click');
      return;
    }

    // 清除之前的 timer
    if (exportTimeoutRef.current) {
      clearTimeout(exportTimeoutRef.current);
    }

    // 設定 debounce
    exportTimeoutRef.current = setTimeout(async () => {
      // 轉換 Set 為 Array（TableIndex 傳入的是 Set）
      const idsArray: string[] = selectedIds instanceof Set ? Array.from(selectedIds) : selectedIds;

      // 驗證是否有選擇項目
      if (!idsArray || idsArray.length === 0) {
        antMessage.warning(t(keys.noSelection) as string);
        return;
      }

      // 設定匯出中狀態
      isExportingRef.current = true;

      try {
        let result: ExportActionResult;
        // Service Mode
        result = await action(idsArray);

        if (result.success) {
          const { job_id, message: msg } = result.data as { job_id: string; message?: string };

          console.log('[useExport] Export success:', { job_id, jobName, hasAddJob: !!addJob });

          // 如果有背景任務管理，添加任務
          if (addJob && typeof addJob === 'function') {
            console.log('[useExport] Adding job to background manager:', job_id, jobName);
            addJob(job_id, jobName);
          } else {
            console.warn('[useExport] addJob function not available:', addJob);
          }

          antMessage.success(msg || (t(keys.success) as string));
        } else {
          antMessage.error(result.message || (t(keys.error) as string));
        }
      } catch (error) {
        console.error('Export error:', error);
        antMessage.error(t(keys.error) as string);
      } finally {
        // 延遲重置匯出狀態，避免快速連續點擊
        setTimeout(() => {
          isExportingRef.current = false;
        }, 1000);
      }
    }, debounceDelay);
  }, [action, addJob, jobName, t, i18n.language, keys, debounceDelay]);

  return handleExport;
}

export default useExport;
